/**
 * API endpoints para Payroll (Nómina)
 * Ahora parte del módulo de empleados (RRHH)
 * Base URL: /api/employees/
 */

import { DELETE, GET, PATCH, POST, PUT } from "../config";

/**
 * =================================
 * PERÍODOS DE NÓMINA
 * =================================
 */

/**
 * Listar períodos de nómina
 */
export const listPeriods = (params = {}) => {
  return GET("employees/payroll-periods/", { params });
};

/**
 * Obtener detalle de un período de nómina
 */
export const getPeriod = (id) => {
  return GET(`employees/payroll-periods/${id}/`);
};

/**
 * Crear período de nómina
 */
export const createPeriod = (data) => {
  return POST("employees/payroll-periods/", data);
};

/**
 * Actualizar período de nómina
 */
export const updatePeriod = (id, data) => {
  return PUT(`employees/payroll-periods/${id}/`, data);
};

/**
 * Actualizar parcialmente período de nómina
 */
export const partialUpdatePeriod = (id, data) => {
  return PATCH(`employees/payroll-periods/${id}/`, data);
};

/**
 * Eliminar período de nómina
 */
export const deletePeriod = (id) => {
  return DELETE(`employees/payroll-periods/${id}/`);
};

/**
 * Calcular nómina para todos los empleados activos del período
 */
export const calculatePayroll = (periodId) => {
  return POST(`employees/payroll-periods/${periodId}/calculate_payroll/`);
};

/**
 * Procesar todos los pagos del período
 */
export const processAllPayments = (periodId, data = {}) => {
  return POST(`employees/payroll-periods/${periodId}/process_all_payments/`, data);
};

/**
 * Obtener resumen del período
 */
export const getPeriodSummary = (periodId) => {
  return GET(`employees/payroll-periods/${periodId}/summary/`);
};

/**
 * =================================
 * ENTRADAS DE NÓMINA
 * =================================
 */

/**
 * Listar entradas de nómina
 */
export const listEntries = (params = {}) => {
  return GET("employees/payroll-entries/", { params });
};

/**
 * Obtener detalle de una entrada de nómina
 */
export const getEntry = (id) => {
  return GET(`employees/payroll-entries/${id}/`);
};

/**
 * Crear entrada de nómina
 */
export const createEntry = (data) => {
  return POST("employees/payroll-entries/", data);
};

/**
 * Actualizar entrada de nómina
 */
export const updateEntry = (id, data) => {
  return PUT(`employees/payroll-entries/${id}/`, data);
};

/**
 * Actualizar parcialmente entrada de nómina
 */
export const partialUpdateEntry = (id, data) => {
  return PATCH(`employees/payroll-entries/${id}/`, data);
};

/**
 * Eliminar entrada de nómina
 */
export const deleteEntry = (id) => {
  return DELETE(`employees/payroll-entries/${id}/`);
};

/**
 * Aplicar descuentos a una entrada de nómina
 */
export const applyDeductions = (entryId, deductions) => {
  return POST(`employees/payroll-entries/${entryId}/apply_deductions/`, {
    deductions,
  });
};

/**
 * Aplicar bonos a una entrada de nómina
 */
export const applyBonuses = (entryId, bonuses) => {
  return POST(`employees/payroll-entries/${entryId}/apply_bonuses/`, {
    bonuses,
  });
};

/**
 * Obtener egresos del período de nómina
 */
export const getPeriodExpenses = (periodId) => {
  return GET(`employees/payroll-periods/${periodId}/expenses/`);
};

/**
 * Procesar pago de una entrada de nómina
 */
export const processPayment = (entryId, data = {}) => {
  return POST(`employees/payroll-entries/${entryId}/process_payment/`, data);
};

/**
 * =================================
 * PAGOS DE NÓMINA
 * =================================
 */

/**
 * Listar pagos de nómina
 */
export const listPayments = (params = {}) => {
  return GET("employees/payroll-payments/", { params });
};

/**
 * Obtener detalle de un pago de nómina
 */
export const getPayment = (id) => {
  return GET(`employees/payroll-payments/${id}/`);
};

/**
 * Exportar objeto con todas las funciones
 */
const payrollApi = {
  // Períodos
  periods: {
    list: listPeriods,
    get: getPeriod,
    create: createPeriod,
    update: updatePeriod,
    partialUpdate: partialUpdatePeriod,
    delete: deletePeriod,
    calculatePayroll,
    processAllPayments,
    getSummary: getPeriodSummary,
    getExpenses: getPeriodExpenses,
    close: deletePeriod, // Alias para cerrar (eliminar)
    processPayroll: calculatePayroll, // Alias para procesar
  },
  // Entradas
  entries: {
    list: listEntries,
    get: getEntry,
    create: createEntry,
    update: updateEntry,
    partialUpdate: partialUpdateEntry,
    delete: deleteEntry,
    applyDeductions,
    applyBonuses,
    processPayment,
    pay: processPayment, // Alias para pagar entrada
  },
  // Pagos
  payments: {
    list: listPayments,
    get: getPayment,
  },
};

export default payrollApi;
